import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { Tooltip } from '@bedrock/components';
import { TooltipProps } from '@bedrock/components/lib/Tooltip';
import { useScreenAdapterContext } from '@bedrock/eed-react';
import classNames from 'classnames';
import './index.less';

export interface EllipsisTooltipProps extends React.HTMLAttributes<HTMLDivElement> {
  clamp?: number;
  className?: string;
  wrapperClassName?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  text?: ReactNode;
  tooltipProps?: Omit<TooltipProps, 'overlay'>;
  title?: string;
  noTooltip?: boolean;
  /** 搜索高亮关键词，匹配部分加粗并使用 --primary-1 颜色 */
  keyword?: string;
}

const CLS_PREFIX = 'ellipsis-tooltip';

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = (props) => {
  const { viewBase = 1 } = useScreenAdapterContext();

  const { className, clamp = 1, disabled, style, text, tooltipProps, title, wrapperClassName, noTooltip, keyword, ...restProps } = props;
  const [visible, setVisible] = useState(false);

  const measureRef = useRef<HTMLDivElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);

  const handleVisibleChange = useCallback(
    (newVisible: boolean) => {
      if (disabled) {
        setVisible(newVisible);
        return;
      }

      if (newVisible && !tooltipProps?.disabled) {
        const { height: displayHeight = 0, width } = displayRef.current?.getBoundingClientRect() || {};
        measureRef.current.style.width = width + 'px';
        const { height: measureHeight = 0 } = measureRef.current?.getBoundingClientRect() || {};

        if (measureHeight !== displayHeight) {
          setVisible(newVisible);
        }
      } else {
        setVisible(newVisible);
      }
    },
    [disabled, tooltipProps?.disabled],
  );

  /** 高亮处理：将 text 中匹配 keyword 的部分加粗并变色 */
  const highlightedText = useMemo(() => {
    if (!keyword || typeof text !== 'string') return text;
    const lowerText = text.toLowerCase();
    const lowerKeyword = keyword.toLowerCase();
    const idx = lowerText.indexOf(lowerKeyword);
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + keyword.length);
    const after = text.slice(idx + keyword.length);
    return (
      <>
        {before}
        <span style={{ fontWeight: 'bold', color: 'var(--primary-1)' }}>{match}</span>
        {after}
      </>
    );
  }, [text, keyword]);

  if (!text) return '-';

  const content = (
    <div className={classNames(`${CLS_PREFIX}-wrapper`, wrapperClassName)}>
      <div
        className={classNames(`${CLS_PREFIX}-for-measure fixed`, className)}
        ref={measureRef}
        style={{ ...style, WebkitLineClamp: clamp + 1 }}>
        {highlightedText}
      </div>
      <div
        {...restProps}
        className={classNames(`${CLS_PREFIX}-for-display`, className)}
        ref={displayRef}
        style={{ ...style, WebkitLineClamp: clamp }}>
        {highlightedText}
      </div>
    </div>
  )

  if (noTooltip) {
    return (
      content
    )
  }

  return (
    <Tooltip
      {...tooltipProps}
      overlayClassName={classNames(`${CLS_PREFIX}-overlay`, tooltipProps?.overlayClassName)}
      overlayStyle={{
        ...tooltipProps?.overlayStyle,
        maxWidth: tooltipProps?.overlayStyle?.maxWidth || 480 * viewBase,
        // @ts-ignore
        '--content-max-height': `${tooltipProps?.overlayStyle?.maxHeight || 480 * viewBase}px`,
      }}
      title={title || text}
      visible={visible}
      onVisibleChange={handleVisibleChange}>
      {content}
    </Tooltip>
  );
};